// TechnicianLocationObserver for real-time technician location tracking
import { makeAutoObservable } from "mobx";
import { supabase } from "../utils/supabase";
const makeKey = (id) => `technician_${id}`;
export class TechnicianLocationObserver {
    constructor(mapService) {
        this.mapService = mapService;
        // Observable state
        this.isActive = false;
        this.error = null;
        this.technicianLocations = new Map();
        this.channel = null;
        makeAutoObservable(this);
    }
    // Start observing technician location changes
    async startObserving(idTechnician) {
        if (this.isActive) {
            console.warn("TechnicianLocationObserver is already active");
            return;
        }
        try {
            this.setError(null);
            // Try to load initial locations (non-fatal if map is not ready yet)
            try {
                await this.loadInitialLocations(idTechnician);
            }
            catch (initError) {
                console.warn("Initial technician location load failed (map may not be ready yet):", initError);
            }
            // Always set up real-time subscription for future updates
            await this.setupRealtimeSubscription();
            this.setActive(true);
        }
        catch (error) {
            this.setError("Failed to start technician location observer");
            console.error("Error starting technician location observer:", error);
            throw error;
        }
    }
    // Stop observing technician location changes
    stopObserving() {
        if (!this.isActive) {
            console.warn("TechnicianLocationObserver is not active");
            return;
        }
        try {
            // Remove realtime subscription
            if (this.channel) {
                supabase().removeChannel(this.channel);
                this.channel = null;
            }
            // Clear all technician locators from map
            this.clearAllTechnicianLocators();
            this.setActive(false);
            this.setError(null);
        }
        catch (error) {
            console.error("Error stopping technician location observer:", error);
        }
    }
    // Load initial technician locations from database
    async loadInitialLocations(idTechnician) {
        try {
            const { data, error } = await supabase()
                .from("technician_locations")
                .select(`
          *,
          technician:employees!technician_id (
            id,
            firstName,
            lastName,
            email
          )
        `)
                .eq("technician_id", idTechnician)
                .order("created_at", { ascending: false })
                .limit(1);
            if (error) {
                throw new Error(`Failed to load technician locations: ${error.message}`);
            }
            if (data && data.length > 0) {
                // Clear existing locations
                this.technicianLocations.clear();
                const locationData = data[0];
                const technicianLocation = locationData;
                this.technicianLocations.set(technicianLocation.technician_id, technicianLocation);
                // Add locator to map
                await this.addTechnicianLocator(technicianLocation);
            }
            else {
                console.warn(`No location data found for technician: ${idTechnician}`);
            }
        }
        catch (error) {
            console.error("Error loading initial technician locations:", error);
            throw error;
        }
    }
    // Set up real-time subscription to technician_locations table
    async setupRealtimeSubscription() {
        try {
            this.channel = supabase()
                .channel("technician_locations_changes")
                .on("postgres_changes", {
                event: "*", // Listen to all events (INSERT, UPDATE, DELETE)
                schema: "public",
                table: "technician_locations",
            }, (payload) => this.handleLocationChange(payload))
                .subscribe((status) => {
                if (status === "SUBSCRIBED") {
                    // subscribed
                }
                else if (status === "CHANNEL_ERROR") {
                    this.setError("Failed to subscribe to technician location changes");
                }
            });
        }
        catch (error) {
            console.error("Error setting up realtime subscription:", error);
            throw error;
        }
    }
    // Handle real-time location change events
    async handleLocationChange(payload) {
        try {
            const { eventType, new: newRecord, old: oldRecord } = payload;
            switch (eventType) {
                case "INSERT": {
                    // Append-only table: each INSERT is a new GPS reading
                    const existing = this.technicianLocations.get(newRecord.technician_id);
                    if (existing) {
                        await this.updateTechnicianLocator(newRecord);
                    }
                    else {
                        // New technician not yet on map — add them
                        this.technicianLocations.set(newRecord.technician_id, newRecord);
                        try {
                            await this.addTechnicianLocator(newRecord);
                        }
                        catch (e) {
                            console.warn("Failed to add new technician locator:", e);
                        }
                    }
                    break;
                }
                case "UPDATE":
                    await this.updateTechnicianLocator(newRecord);
                    break;
                case "DELETE":
                    await this.handleLocationDelete(oldRecord);
                    break;
                default:
                    console.warn("Unknown event type:", eventType);
            }
        }
        catch (error) {
            console.error("Error handling location change:", error);
            this.setError("Failed to process location change");
        }
    }
    // Handle location delete
    async handleLocationDelete(locationData) {
        try {
            // Remove from local state
            this.technicianLocations.delete(locationData.technician_id);
            // Remove from map
            await this.removeTechnicianLocator(locationData.technician_id);
        }
        catch (error) {
            console.error("Error handling location delete:", error);
            throw error;
        }
    }
    // Add technician locator to map
    async addTechnicianLocator(location) {
        try {
            const locator = {
                id: makeKey(location.technician_id),
                position: {
                    latitude: location.latitude,
                    longitude: location.longitude,
                    address: `Lat: ${location.latitude}, Lng: ${location.longitude}`,
                },
                title: location.technician
                    ? `${location.technician.firstName} ${location.technician.lastName}`
                    : `Technician ${location.technician_id}`,
                description: location.technician?.email || undefined,
                icon: "🧑‍🔧", // Technician emoji
                isVisible: true,
            };
            await this.mapService.addLocator(locator);
        }
        catch (error) {
            console.error("Error adding technician locator:", error);
            throw error;
        }
    }
    // Update technician locator on map
    async updateTechnicianLocator(location) {
        if (!this.technicianLocations.get(location.technician_id)) {
            console.warn(`Technician ${location.technician_id} not found in local state`);
            return;
        }
        try {
            const oldLocator = this.mapService.getLocator(makeKey(location.technician_id));
            if (oldLocator) {
                const updates = {
                    ...oldLocator,
                    position: {
                        latitude: location.latitude,
                        longitude: location.longitude,
                        address: `Lat: ${location.latitude}, Lng: ${location.longitude}`,
                    },
                };
                await this.mapService.updateLocator(makeKey(location.technician_id), updates);
            }
        }
        catch (error) {
            console.error("Error updating technician locator:", error);
            throw error;
        }
    }
    // Remove technician locator from map
    async removeTechnicianLocator(technicianId) {
        try {
            await this.mapService.removeLocator(makeKey(technicianId));
        }
        catch (error) {
            console.error("Error removing technician locator:", error);
            throw error;
        }
    }
    // Clear all technician locators from map
    async clearAllTechnicianLocators() {
        try {
            const locatorIds = Array.from(this.technicianLocations.keys()).map((technicianId) => makeKey(technicianId));
            for (const locatorId of locatorIds) {
                try {
                    await this.mapService.removeLocator(locatorId);
                }
                catch (error) {
                    console.warn(`Failed to remove locator ${locatorId}:`, error);
                }
            }
            this.technicianLocations.clear();
        }
        catch (error) {
            console.error("Error clearing technician locators:", error);
            throw error;
        }
    }
    // Private state management methods
    setActive(active) {
        this.isActive = active;
    }
    setError(error) {
        this.error = error;
    }
    // Cleanup method
    destroy() {
        this.stopObserving();
        this.technicianLocations.clear();
        this.setError(null);
    }
}
